/** @jsxImportSource @emotion/react */
import { Banner } from '@app/components/Guide/ui/Banner'
import { EmptyData } from '@app/components/ui/EmptyData/EmptyData'
import { isLoadableError } from '@app/state/jotai.utils'
import { Box, Container, Tab, Tabs } from '@mui/material'
import { useAtomValue } from 'jotai'
import { loadable } from 'jotai/utils'
import { DatabaseIcon } from 'lucide-react'
import { useMemo } from 'react'
import {
  Outlet,
  Link as RouterLink,
  useLocation,
  useMatch,
  useResolvedPath,
} from 'react-router-dom'
import { layersAtom } from '@app/domains/layers/state/layers.atoms'
import * as styles from '../styles/DataLayout.styles'
import { FEATURE_FLAGS } from '@app/app-constants'
import { useFeatureFlags } from '@app/hooks/useFeatureFlags'

const layersLoadable = loadable(layersAtom)

const DataLayout = () => {
  const layers = useAtomValue(layersLoadable)
  const location = useLocation()
  const basePath = useResolvedPath('.').pathname
  const isDataRiskDetailPage = useMatch('/app/home/data/detail/:id')
  const { useFeatureFlag } = useFeatureFlags()
  const isHazardReferenceEnabled = useFeatureFlag(FEATURE_FLAGS.HAZARD_REFERENCE)

  const normalizePath = (p: string) => (p.length > 1 ? p.replace(/\/+$/, '') : p)

  const base = normalizePath(basePath)
  const current = normalizePath(location.pathname)

  const tabLink = useMemo(
    () => ({
      all: base,
      elements: `${base}/elements`,
      hazards: `${base}/hazards`,
      contextual: `${base}/contextual`,
      reference: `${base}/reference`,
    }),
    [base],
  )

  if (isLoadableError(layers)) {
    return <EmptyData title="Error loading layers" icon={<DatabaseIcon />} />
  }

  return (
    <Box data-testid="risk-data-layout" css={styles.layoutWrapper}>
      <Box css={styles.container}>
        {/* <Banner header="Data" subheader="Explore your organisation's datasets." /> */}
        {!isDataRiskDetailPage && (
          <Box css={styles.hazardTableContainer}>
            <Box css={styles.tabsWrapper}>
              <Tabs value={current}>
                <Tab label="All" value={tabLink.all} component={RouterLink} to="" replace />
                <Tab
                  label="Elements"
                  value={tabLink.elements}
                  component={RouterLink}
                  to="elements"
                  replace
                />
                <Tab
                  label="Hazards"
                  value={tabLink.hazards}
                  component={RouterLink}
                  to="hazards"
                  replace
                />
                <Tab
                  label="Contextual"
                  value={tabLink.contextual}
                  component={RouterLink}
                  to="contextual"
                  replace
                />
                {isHazardReferenceEnabled && (
                  <Tab
                    label="Reference"
                    value={tabLink.reference}
                    component={RouterLink}
                    to="reference"
                    replace
                  />
                )}
              </Tabs>
            </Box>
          </Box>
        )}

        <Box data-testid="risk-data-layout-outlet">
          <Container css={styles.hazardTableContainer}>
            <Outlet />
          </Container>
        </Box>
      </Box>
    </Box>
  )
}

export default DataLayout
